/**
 * Who else is in this chain, and where their pointer is.
 *
 * A chain of title is worked by more than one person — an agent drawing
 * it and an examiner reading it, or two agents on neighbouring tracts of
 * the same section — and the diagram already refuses a save when
 * somebody else has moved on. Refusing is the safety net; seeing each
 * other is how it stops being needed. A cursor with a name on it is the
 * difference between "somebody else has changed this" and "Ada is on the
 * Peterman deed, leave it alone for a minute".
 *
 * THE POSITION IS IN WORLD COORDINATES, never in pixels. Everybody has
 * their own pan and zoom, so a cursor sent in screen pixels would land
 * on a different box for every person looking at it — which is worse
 * than no cursor, because it is confidently wrong.
 *
 * The rules here are the ones that go wrong quietly: how often to send,
 * when to stop, when to believe somebody has gone, and what colour they
 * are. All of it is pure so it can be tested without a socket.
 */

/** The channel for a chain. Same anchor rules as the diagram itself. */
export function chartTopic({ projectId = null, assignmentId = null, tractId = null } = {}) {
  if (projectId) return `chart:p:${projectId}${tractId ? `:t:${tractId}` : ''}`;
  if (assignmentId) return `chart:a:${assignmentId}`;
  return null;
}

/**
 * Ten colours, picked so that any two are told apart at cursor size.
 *
 * Assigned from the person's id rather than from the order they arrive,
 * so somebody is the same colour for everybody at once and stays that
 * colour when they reload. Order-based colours change under you when
 * anybody leaves, which on a chart people join and leave all day is a
 * cursor that keeps changing identity.
 */
export const CURSOR_COLOURS = [
  '#e8543f', '#2f6df6', '#12805c', '#b8309e', '#c47f10',
  '#0f8ab0', '#7a4ae0', '#c22e5c', '#3f7a1f', '#6b5b3e',
];

export function cursorColour(id) {
  const said = String(id || '');
  let sum = 0;
  for (let i = 0; i < said.length; i += 1) sum = (sum * 31 + said.charCodeAt(i)) % 100000;
  return CURSOR_COLOURS[sum % CURSOR_COLOURS.length];
}

/**
 * What to call somebody on a cursor.
 *
 * Their name, and NEVER their email. An address is not a label — it is
 * a way to reach a person, and putting one on everybody's screen hands
 * it to whoever else happens to be working the chain. Somebody with no
 * name set is "Someone", which says exactly as much as is known.
 */
export function cursorName(profile) {
  const said = String(profile?.name || '').trim();
  if (!said) return 'Someone';
  // A first name is enough on a chart and fits beside the arrow; the
  // full name is on the roster for anybody who needs it.
  return said.length > 18 ? `${said.slice(0, 17)}…` : said;
}

// Fast enough to read as live, slow enough not to flood a channel: about
// twenty a second while the pointer is moving, and nothing at all while
// it is still.
export const SEND_EVERY_MS = 50;

// A pointer nobody has heard from for this long is treated as gone even
// if the channel still lists them, because a laptop that went to sleep
// mid-drag leaves a cursor sitting on a box forever.
export const STALE_AFTER_MS = 12000;

/** Has enough time passed, and has the pointer actually moved? */
export function shouldSend(last, at, now) {
  if (!at) return false;
  if (!last) return true;
  if (now - last.at < SEND_EVERY_MS) return false;
  return Math.abs(last.x - at.x) > 0.5 || Math.abs(last.y - at.y) > 0.5;
}

/**
 * Fold one message into what is known about everybody.
 *
 * A message with no position means the pointer left the canvas: the
 * person is still here, so they stay in the list, but their cursor is
 * not drawn anywhere. Dropping them entirely would make them flicker
 * back into existence the moment they moved again.
 */
export function withCursor(peers, message, now) {
  if (!message?.id) return peers;
  const next = new Map(peers);
  const was = next.get(message.id);
  next.set(message.id, {
    id: message.id,
    name: message.name || was?.name || 'Someone',
    x: Number.isFinite(message.x) ? message.x : null,
    y: Number.isFinite(message.y) ? message.y : null,
    at: now,
  });
  return next;
}

/**
 * Everybody the channel still lists, keeping what is known about them.
 *
 * Takes { id, name } rather than bare ids, because THE CHANNEL ALREADY
 * KNOWS THE NAME — it is tracked when somebody joins. Reading it only
 * off the cursor messages meant a person who was in the chain but had
 * not moved their pointer yet showed as "Someone", which is exactly
 * backwards: the moment they arrive is the moment you want to know who
 * they are.
 */
export function withMembers(peers, members, now) {
  const next = new Map();
  for (const member of members || []) {
    const id = typeof member === 'string' ? member : member?.id;
    if (!id) continue;
    const was = peers.get(id);
    const named = (typeof member === 'object' && member?.name) || was?.name || 'Someone';
    next.set(id, was
      ? { ...was, name: named }
      : { id, name: named, x: null, y: null, at: now });
  }
  return next;
}

/** The cursors worth drawing: here, placed, and heard from recently. */
export function drawable(peers, now) {
  const out = [];
  for (const peer of peers.values()) {
    if (peer.x === null || peer.y === null) continue;
    if (now - peer.at > STALE_AFTER_MS) continue;
    out.push(peer);
  }
  // A stable order, so React never reorders one cursor onto another's
  // key and swaps two people's colours mid-move.
  return out.sort((a, b) => (a.id < b.id ? -1 : 1));
}

/** "Ada", "Ada and Roy", "Ada, Roy and 2 others". */
export function whoElse(peers) {
  const names = [...peers.values()].map((p) => p.name);
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} other${names.length === 3 ? '' : 's'}`;
}
